import { useEffect, useMemo, useState } from "react";
import { PackageX, FileSpreadsheet, FileText, RefreshCw, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function buildMonthOptions(span = 12) {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < span; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ value, label: `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
}

export default function StockRemovalReport() {
  const monthOptions = useMemo(() => buildMonthOptions(12), []);
  const [month, setMonth] = useState(monthOptions[0]?.value || "all"); // mois courant par défaut
  const [summary, setSummary] = useState({ count: 0, quantite_totale: 0, periode: "—" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // "csv" | "pdf" | null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/equipment-returns/stock-removals/report", {
        params: { month, format: "json" },
      });
      setItems(data.items || []);
      setSummary(data.summary || { count: 0, quantite_totale: 0, periode: "—" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger le rapport.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [month]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const resp = await api.get("/equipment-returns/stock-removals/report", {
        params: { month, format },
        responseType: "blob",
      });
      const ext = format === "csv" ? "csv" : "pdf";
      const safe = (month || "tous").replace("/", "-");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(resp.data);
      link.download = `sorties_definitives_${safe}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Rapport ${ext.toUpperCase()} téléchargé.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur d'export.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="bg-gradient-to-br from-rose-50/60 to-white border border-rose-200 rounded-sm p-4 space-y-3" data-testid="stock-removal-report">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5 text-rose-700">
            <PackageX size={11} /> Rapport — Sorties définitives du stock
          </p>
          <p className="text-xs text-zinc-600 font-mono">
            Liste des matériels définitivement retirés du stock (endommagés / inutilisables). Sources : restitutions collaborateurs + enlèvements sites.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label htmlFor="report-month" className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
              <CalendarIcon size={10} className="inline mr-1" /> Période
            </label>
            <select
              id="report-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-8 border border-zinc-300 rounded-sm px-2 text-xs font-mono bg-white"
              data-testid="stock-removal-month-select"
            >
              {monthOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              <option value="all">Toutes périodes</option>
            </select>
          </div>
          <Button
            type="button" size="sm" variant="outline"
            onClick={load} disabled={loading}
            data-testid="stock-removal-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin mr-1" : "mr-1"} />
            Actualiser
          </Button>
          <Button
            type="button" size="sm"
            onClick={() => exportFile("csv")}
            disabled={exporting === "csv" || summary.count === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="stock-removal-export-csv"
          >
            <FileSpreadsheet size={13} className="mr-1" />
            {exporting === "csv" ? "Export…" : "CSV"}
          </Button>
          <Button
            type="button" size="sm"
            onClick={() => exportFile("pdf")}
            disabled={exporting === "pdf" || summary.count === 0}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="stock-removal-export-pdf"
          >
            <FileText size={13} className="mr-1" />
            {exporting === "pdf" ? "Export…" : "PDF"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="bg-white border border-rose-200 rounded-sm p-3">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Période</p>
          <p className="text-sm font-bold text-zinc-900 mt-1" data-testid="stock-removal-stat-period">{summary.periode}</p>
        </div>
        <div className="bg-white border border-rose-200 rounded-sm p-3">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Sorties</p>
          <p className="text-2xl font-extrabold text-rose-700 mt-0.5" data-testid="stock-removal-stat-count">{summary.count}</p>
        </div>
        <div className="bg-white border border-rose-200 rounded-sm p-3">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Unités</p>
          <p className="text-2xl font-extrabold text-rose-700 mt-0.5" data-testid="stock-removal-stat-qty">{summary.quantite_totale}</p>
        </div>
      </div>

      {summary.count > 0 && (
        <details className="bg-white border border-rose-100 rounded-sm">
          <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider p-2 text-zinc-700 hover:bg-rose-50">
            Aperçu rapide ({Math.min(items.length, 5)} dernière(s))
          </summary>
          <ul className="text-xs divide-y divide-rose-100" data-testid="stock-removal-preview-list">
            {items.slice(0, 5).map((r, idx) => (
              <li key={idx} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
                <span className="col-span-3 font-mono text-zinc-500">
                  {r.date_sortie ? new Date(r.date_sortie).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </span>
                <span className="col-span-4 font-medium truncate">{r.designation}</span>
                <span className="col-span-1 font-mono text-rose-700 font-bold">×{r.quantite}</span>
                <span className="col-span-2 text-zinc-600">{r.etat}</span>
                <span className="col-span-2 text-zinc-600 truncate">{r.cible}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
